import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2, Trash2 } from 'lucide-react'

import { api, enElMostrador, ErrorApi } from '@/lib/api'
import { aNumero, cantidad as fmtCantidad } from '@/lib/formato'
import { Campo } from '@/componentes/ui/Campo'
import { Modal } from '@/componentes/ui/Modal'
import { LookupSelect, type ItemLookup } from '@/componentes/ui/LookupSelect'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'
import { Ulid } from '@/lib/ulid'

// El día del LOCAL y no el del navegador: es la fecha con la que nace la
// declaración, y el reporte de merma agrupa por ella. Con el día del navegador,
// una PC de mostrador con el reloj corrido archiva la pérdida en otro día.
import { hoy } from '@/lib/rango'

interface Props {
  onCerrar: () => void
  onDeclarada: () => void
}


// Declarar merma.
//
// Tres campos y listo: qué, cuánto y por qué. Cada campo de más es una razón
// más para no cargarla, y una merma que no se carga es mercadería que sale del
// inventario sin dejar rastro.
export function DeclararMerma({ onCerrar, onDeclarada }: Props) {
  useAyudaDe('merma.declarar')

  const [producto, setProducto] = useState<ItemLookup | null>(null)
  const [cantidad, setCantidad] = useState('')
  // El motivo entero y no solo el id: la caja congela el NOMBRE al declarar
  // (ver ServicioTurnoLocal), así que lo tiene que mandar.
  const [motivo, setMotivo] = useState<ItemLookup | null>(null)
  const [detalle, setDetalle] = useState('')
  const [fecha, setFecha] = useState(hoy)
  const [error, setError] = useState<string | null>(null)

  // El ULID se genera UNA vez, al abrir el diálogo, y no al mandar: es la
  // clave de idempotencia, y generarla al mandar la hacía inútil justo en el
  // caso para el que existe — el que declara ve un error, vuelve a apretar, y
  // si la primera se guardó la merma queda declarada dos veces. Va a los DOS
  // hosts: la caja la usa además como clave de la cola de salida.
  const [ulid] = useState(() => Ulid.nuevo())

  const declarar = useMutation({
    mutationFn: () => {
      // Se manda solo si NO es hoy: mandar la fecha de hoy sin hora pondría
      // toda la merma del día a la medianoche.
      const fechaAt = fecha === hoy() ? null : `${fecha}T12:00:00Z`
      const detalleLimpio = detalle.trim() || null

      // Los dos hosts guardan la misma merma pero con formas distintas, y
      // ninguna de las dos se puede acomodar a la otra: la caja lleva la
      // cantidad en MILÉSIMAS enteras (ver el contrato de sincronización) y
      // congela el nombre del motivo porque no tiene la tabla de la nube. La
      // pantalla es una sola; el que se adapta es el payload.
      //
      // La pregunta es en qué ÁREA está parada la persona y no en qué programa
      // corre: este mismo diálogo se abre desde el turno —«lo que tiré en esta
      // caja», que se encola y anda sin internet— y desde la pantalla Merma de
      // Gestión, que en el ejecutable habla con la nube por el puente. Con
      // `esCaja()` a secas, la merma declarada desde Gestión salía con el
      // payload del mostrador y la nube la rechazaba.
      if (enElMostrador()) {
        return api.post('/mermas', {
          // La misma clave que en la nube: la caja también la usa para no
          // declarar dos veces la misma merma, y de paso es la clave con la que
          // la operación viaja en la cola de salida.
          ulid,
          productoIdNube: producto?.id,
          cantidadMilesimas: Math.round((aNumero(cantidad) ?? 0) * 1000),
          motivoId: motivo?.id,
          motivoNombre: motivo?.nombre,
          detalle: detalleLimpio,
          fechaAt,
        })
      }

      return api.post('/mermas', {
        ulid,
        productoId: producto?.id,
        cantidad: aNumero(cantidad),
        motivoId: motivo?.id,
        detalle: detalleLimpio,
        fechaAt,
      })
    },
    onSuccess: onDeclarada,
    onError: (e: ErrorApi) => setError(e.message),
  })

  const valor = aNumero(cantidad)

  // La abreviatura viene en el meta del lookup, no en el detalle: el detalle
  // es «$1.450,00 / kg» y acá hace falta solo «kg».
  const unidad = typeof producto?.meta?.unidad === 'string' ? producto.meta.unidad : ''

  return (
    <Modal
      titulo="Declarar merma"
      descripcion="Lo que se tiró, se regaló o se remató."
      onCerrar={onCerrar}
      ancho="md"
      pie={
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCerrar} className="btn-secundario btn-sm">
            Cancelar
          </button>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => {
              setError(null)

              if (!producto) return setError('Elegí el producto.')
              if (valor == null || valor <= 0) return setError('Poné cuánto se perdió.')
              if (!motivo) return setError('Elegí por qué se perdió.')

              declarar.mutate()
            }}
            disabled={declarar.isPending}
            className="btn-peligro btn-sm"
          >
            {declarar.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Declarar
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <LookupSelect
          fuente="productos"
          etiqueta="Qué se perdió"
          valor={producto?.id ?? null}
          onCambio={(_, item) => {
            setProducto(item)
            setError(null)
          }}
          placeholder="Buscá el producto"
          requerido
        />

        <Campo
          etiqueta="Cuánto"
          value={cantidad}
          onChange={(e) => {
            setCantidad(e.target.value)
            setError(null)
          }}
          mono
          placeholder="0,000"
          // La unidad al lado del número: «2,5» no dice nada sin saber si son
          // kilos o atados, y es la confusión que hace que se declare mal.
          sufijo={
            unidad ? <span className="pr-3 text-[13px] text-tinta-tenue">{unidad}</span> : undefined
          }
          ayuda={
            valor != null && unidad
              ? `${fmtCantidad(valor, valor === Math.trunc(valor) ? 0 : 3)} ${unidad}`
              : undefined
          }
        />

        <LookupSelect
          fuente="catalogo/MOTIVO_MERMA"
          etiqueta="Por qué"
          valor={motivo?.id ?? null}
          onCambio={(_, item) => {
            setMotivo(item)
            setError(null)
          }}
          placeholder="Se pasó, golpeado, regalado…"
          requerido
        />

        {/* La fecha se puede correr porque la merma se anota cuando hay un
            rato: el cajón se tira a las nueve y se carga a las once. Sin esto,
            el reporte por día pone todo en el momento en que alguien se
            acordó. */}
        <label className="block max-w-[200px]">
          <span className="etiqueta">Cuándo se tiró</span>
          <input
            type="date"
            className="campo"
            value={fecha}
            max={hoy()}
            onChange={(e) => setFecha(e.target.value)}
          />
        </label>

        <Campo
          etiqueta="Detalle (opcional)"
          value={detalle}
          onChange={(e) => setDetalle(e.target.value)}
          placeholder="Lo que haga falta aclarar."
        />

        {error && <p className="error">{error}</p>}

        {/* Se dice de frente para qué sirve el registro: sin eso, cargar la
            merma se siente como trabajo administrativo y no se hace. */}
        <p className="rounded-lg border border-borde bg-crema-alt px-3.5 py-2.5 text-[12.5px] leading-snug text-tinta-suave">
          La merma es el segundo costo del negocio después de la mercadería, y el único que se puede
          bajar comprando distinto. Declarándola, el reporte dice qué proporción de cada producto se
          termina tirando — que es el número con el que se decide comprar menos.
        </p>
      </div>
    </Modal>
  )
}
